import axios from 'axios';

import { API } from '../common';

const TOKEN_KEY = 'contractor_access_token';
const SESSION_KEY = 'contractor_session';
const USERNAME_KEY = 'contractor_username';

export type LoginStartResponseType = {
    email?: string;
    phone?: string;
    username?: string;
    session: string;
};

export type LoginResponseType = {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
};

export class AuthService {
    private static instance: AuthService;

    private constructor() {}

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    private decodeTokenPayload(): { exp?: number; sub?: string } | null {
        const token = this.getToken();
        const payload = token?.split('.')[1];

        if (!payload || typeof globalThis.atob !== 'function') {
            return null;
        }

        try {
            const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
            const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
            return JSON.parse(globalThis.atob(paddedPayload));
        } catch (error) {
            console.error('Failed to parse token:', error);
            return null;
        }
    }

    /**
     * Check if valid token exists
     */
    public hasValidToken(): boolean {
        const token = localStorage.getItem(TOKEN_KEY);
        return !!token && !this.isTokenExpired();
    }

    public hasValidTokenForUser(userSub: string): boolean {
        return this.hasValidToken() && this.getTokenSubject() === userSub;
    }

    public hasTokenForUser(userSub: string): boolean {
        return this.getTokenSubject() === userSub;
    }

    public getTokenSubject(): string | null {
        return this.decodeTokenPayload()?.sub ?? null;
    }

    /**
     * Get access token
     */
    public getToken(): string | null {
        return localStorage.getItem(TOKEN_KEY);
    }

    /**
     * Check if token is expired
     */
    public isTokenExpired(): boolean {
        const payload = this.decodeTokenPayload();
        if (!payload) {
            return true;
        }

        const exp = payload.exp;

        if (!exp) {
            return true;
        }

        // Check if token expires in less than 60 seconds
        const now = Math.floor(Date.now() / 1000);
        return exp - now < 60;
    }

    /**
     * Initiate login process
     */
    public async loginStart(uuid: string): Promise<LoginStartResponseType> {
        try {
            const response = await axios.post<LoginStartResponseType>(`${API.RAPI_URL}/login-start`, {
                username: uuid,
            });

            // Save session and username for subsequent use
            localStorage.setItem(SESSION_KEY, response.data.session);
            localStorage.setItem(USERNAME_KEY, response.data.username ?? uuid);

            return response.data;
        } catch (error) {
            console.error('Login start failed:', error);
            throw error;
        }
    }

    /**
     * Complete login with OTP code
     */
    public async loginComplete(otp: string): Promise<void> {
        const username = localStorage.getItem(USERNAME_KEY);
        const session = localStorage.getItem(SESSION_KEY);

        if (!username || !session) {
            throw new Error('No active login session');
        }

        try {
            const response = await axios.post<LoginResponseType>(
                `${API.RAPI_URL}/login`,
                {
                    username,
                    session,
                    otp,
                },
                { withCredentials: true },
            );

            // Save access token
            localStorage.setItem(TOKEN_KEY, response.data.accessToken);

            // Clear temporary data
            localStorage.removeItem(SESSION_KEY);
        } catch (error) {
            console.error('Login complete failed:', error);
            throw error;
        }
    }

    /**
     * Refresh access token
     */
    public async refreshToken(): Promise<string> {
        try {
            const response = await axios.get<LoginResponseType>(`${API.RAPI_URL}/refresh-token`, {
                withCredentials: true, // Refresh token is sent automatically in cookies
            });

            // Save new access token
            localStorage.setItem(TOKEN_KEY, response.data.accessToken);

            return response.data.accessToken;
        } catch (error) {
            console.error('Token refresh failed:', error);
            this.logout();
            throw error;
        }
    }

    /**
     * Logout user
     */
    public logout(): void {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(USERNAME_KEY);
    }

    /**
     * Get stored contact (email or phone) from session
     */
    public getStoredContact(): string | null {
        return localStorage.getItem(SESSION_KEY);
    }
}

export const authService = AuthService.getInstance();
